import { toDateString } from "utils/date-format";
import { useMemo } from "react";
import { AuditsListResponse, AuditFilterOptions } from "./definitions";
import { CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME } from "models/generateCustomAttributesSchema";
import { optionWithGroupLabel } from "utils/dropdown-utils";
import { Popover } from "@amzn/awsui-components-react";
import { datePopOver, formatDate } from "utils/date-format";
import { GUEST_AUDIT_FIELDS } from "./table-config";
import { sanitizeForCSV } from "utils/validation";

export function customAttributesToCsv(
  obj: any,
  auditCustomAttributes: any,
  uiContent: any
): any {
  const ret: any = {};
  for (const key in obj) {
    if (key !== CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME) {
      if (key === "governmentEntity") {
        const govEntityValue = optionWithGroupLabel(
          uiContent.governmentEntityOptions,
          obj[key],
          // when more than one country option, there will be a country associated with each option
          uiContent.countryOptions && uiContent.countryOptions.length > 1
            ? obj["country"]
            : ""
        );
        ret["governmentEntity"] = sanitizeForCSV(govEntityValue);
      } else {
        ret[key] = sanitizeForCSV(obj[key]);
      }
    } else {
      const customAttributes = obj[key];
      if (auditCustomAttributes && Array.isArray(auditCustomAttributes)) {
        auditCustomAttributes.forEach((attribute: any) => {
          const attributeName = attribute.attributeName;
          if (attribute.inputValueType === "date") {
            ret[attributeName] = customAttributes[attributeName]
              ? toDateString(Number(customAttributes[attributeName]))
              : "";
          } else {
            ret[attributeName] = sanitizeForCSV(
              customAttributes[attributeName] ?? ""
            );
          }
        });
      }
    }
  }
  return ret;
}

export function filterGuestAttributes(obj: any): any {
  const ret: any = {};
  for (const key in obj) {
    if (GUEST_AUDIT_FIELDS.includes(key)) {
      ret[key] = obj[key];
    }
  }
  return ret;
}

export const useCsvData = (
  data: AuditsListResponse | undefined,
  auditCustomAttributes: any,
  uiContent: any,
  isGuest: boolean
) => {
  return useMemo(() => {
    if (!data?.audits.auditList) return { data: [], headers: [] };
    const transformedData = data.audits.auditList.map(({ ...rest }) => {
      rest = customAttributesToCsv(rest, auditCustomAttributes, uiContent);
      rest.auditCloseDate = toDateString(rest.auditCloseDate);
      rest.auditEndDate = toDateString(rest.auditEndDate);
      rest.statuteOfLimitations = toDateString(rest.statuteOfLimitations);
      rest.auditStartDate = toDateString(rest.auditStartDate);
      rest.createdAt = toDateString(rest.createdAt);
      rest.modifiedAt = toDateString(rest.modifiedAt);
      rest.auditInitiationDate = toDateString(rest.auditInitiationDate);
      rest.progressModifiedAt = toDateString(rest.progressModifiedAt);
      if (isGuest) {
        rest = filterGuestAttributes(rest);
      }
      return rest;
    });

    // Collect all possible keys from all items in case some columns are missing for some audits
    const allKeys = new Set<string>();
    transformedData.forEach((item) => {
      Object.keys(item).forEach((key) => allKeys.add(key));
    });
    const headers = Array.from(allKeys)
      .map((key) => ({
        label: key,
        key: key,
      }))
      .sort((a, b) =>
        a.label.toLowerCase().localeCompare(b.label.toLowerCase())
      );

    return { data: transformedData, headers };
  }, [data?.audits.auditList, isGuest]);
};

export const getProgressUpdated = (item: any) => {
  const progressModifiedAt = formatDate(Number(item.progressModifiedAt));
  const progressModifiedAtPopOver = datePopOver(
    Number(item.progressModifiedAt)
  );
  return progressModifiedAt && item?.progressModifiedBy ? (
    <>
      <Popover dismissButton={false} content={progressModifiedAtPopOver}>
        {progressModifiedAt}
      </Popover>{" "}
      by {item.progressModifiedBy}
    </>
  ) : (
    <></>
  );
};

export const getProgressUpdatedString = (item: any) => {
  const progressModifiedAt = formatDate(Number(item.progressModifiedAt));
  return progressModifiedAt && item?.progressModifiedBy
    ? `${progressModifiedAt} by ${item.progressModifiedBy}`
    : "";
};

export const sortFilteringOptions = (
  filteringOptions: readonly any[],
  keys: Set<string>
) => {
  if (!filteringOptions) return [];

  const groupedOptions = filteringOptions.reduce<AuditFilterOptions>(
    (acc, option) => {
      if (!acc[option.propertyKey]) {
        acc[option.propertyKey] = [];
      }
      acc[option.propertyKey].push(option);
      return acc;
    },
    {}
  );
  Object.keys(groupedOptions).forEach((key) => {
    if (!keys || keys.has(key)) {
      groupedOptions[key].sort((a, b) => {
        if (!isNaN(Number(a.value)) && !isNaN(Number(b.value))) {
          return Number(a.value) - Number(b.value);
        }
        return String(a.value).localeCompare(String(b.value));
      });
    }
  });
  return Object.values(groupedOptions).flat();
};
